import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'

// Admin: all courses. ★ marks featured; deactivate/activate inline (courses
// are never deleted). Create/edit are standalone form pages.

export function AdminCoursesPage() {
  const { t } = useTranslation()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState(null)
  const [copiedId, setCopiedId] = useState(null)

  // Shareable enrollment link: opens the course page with the participant
  // picker already open (after login, if needed). Meant for bbso.ro buttons.
  async function copyLink(c) {
    const url = `${window.location.origin}${import.meta.env.BASE_URL}courses/${c.slug}?inscriere=1`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(c.id)
      setTimeout(() => setCopiedId((id) => (id === c.id ? null : id)), 2000)
    } catch {
      window.prompt(t('admin_courses.copy_link'), url)
    }
  }

  function reload() {
    setLoading(true); setError(null)
    api.admin.courses.list()
      .then((d) => setRows(d.courses || []))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !user || user.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user])

  async function flip(c) {
    if (c.active === 1 && !confirm(t('admin_courses.confirm_deactivate', { name: c.name }))) return
    setBusyId(c.id); setError(null)
    try {
      if (c.active === 1) await api.admin.courses.deactivate(c.id)
      else                await api.admin.courses.activate(c.id)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusyId(null) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!user || user.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  return (
    <main className="container">
      <p><Link to="/admin">← {t('admin.title')}</Link></p>
      <h1>{t('admin_courses.title')}</h1>
      <p className="muted">{t('admin_courses.intro')}</p>
      <p><Link to="/admin/courses/new"><button type="button">{t('admin_courses.add')}</button></Link></p>

      {error && <p className="error">{t(`admin_courses.errors.${error}`, { defaultValue: error })}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && rows.length === 0 && <p className="muted">{t('admin_courses.empty_state')}</p>}

      {!loading && rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_courses.col_name')}</th>
              <th>{t('admin_courses.col_category')}</th>
              <th>{t('admin_courses.col_type')}</th>
              <th>{t('admin_courses.col_teacher')}</th>
              <th>{t('admin_courses.col_price')}</th>
              <th>{t('admin_courses.col_seats')}</th>
              <th>{t('admin_courses.col_status')}</th>
              <th>{t('admin_courses.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.featured === 1 && <span title={t('courses.featured')}>★ </span>}
                  <Link to={`/admin/courses/${c.id}/edit`}>{c.name}</Link>
                </td>
                <td>{c.category_name}</td>
                <td>{t(`admin_courses.type_${c.type}`, { defaultValue: c.type })}</td>
                <td>{c.teacher_name || <span className="muted">—</span>}</td>
                <td>{fmtPrice(c.price)}</td>
                <td>{c.capacity !== null ? `${c.enrolled_count}/${c.capacity}` : c.enrolled_count}</td>
                <td>
                  {c.active === 1
                    ? t('admin_courses.status_active')
                    : <span className="muted" title={c.deactivated_at || ''}>{t('admin_courses.status_inactive')}</span>}
                </td>
                <td>
                  <Link to={`/admin/courses/${c.id}/edit`} className="link-button">
                    {t('common.edit')}
                  </Link>
                  <Link to={`/admin/courses/${c.id}/participants`} className="link-button" style={{ marginLeft: '0.5rem' }}>
                    {t('admin_courses.participants')}
                  </Link>
                  <button
                    type="button"
                    className="link-button"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={() => copyLink(c)}
                  >
                    {copiedId === c.id ? t('admin_courses.link_copied') : t('admin_courses.copy_link')}
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    style={{ marginLeft: '0.5rem' }}
                    disabled={busyId === c.id}
                    onClick={() => flip(c)}
                  >
                    {c.active === 1 ? t('admin_courses.deactivate') : t('admin_courses.activate')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
